import React, { useEffect, useState } from 'react';
import { Checkbox, Input, Radio, Button, Snackbar } from 'geniebook-ui/dist';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  USER_GENDER_FEMALE,
  USER_GENDER_MALE,
} from '@utils/constants/user.constants';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { genderType, IBasicInfoData } from '@models/interfaces/user.interface';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { ValidationError } from 'yup';
import { useAtomValue } from 'jotai';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const EC_USERNAME_MIN_CHAR_INVALID = '102-12';
const EC_SPECIAL_CHAR = '102-03';
const EC_INAPPROPRIATE = '102-09';
const EC_USER_USED = '102-01';

const BasicInfoForm = () => {
  const navigate = useNavigate();
  const getUserDetails = useAtomValue(userDetailQueryAtom);
  const [disableButton, setDisableButton] = useState(true);
  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);
  const viewWidth = useViewWidth();
  const [formData, setFormData] = useState<IBasicInfoData>({
    date_of_birth: '',
    show_date_of_birth: 0,
    gender: '1',
  });
  const [originalUserName, setOriginalUserName] = useState<string>('');
  const [newUserName, setNewUserName] = useState<string>('');
  const [userNameChanged, setUserNameChanged] = useState<boolean>(false);

  const checkAllowableCharactersOnly = (string: string) =>
    /^[A-Za-z0-9.\s-]+$/.test(string);

  useEffect(() => {
    if (getUserDetails.isSuccess) {
      setFormData({
        ...formData,
        date_of_birth: getUserDetails?.data?.data.dob,
        show_date_of_birth: getUserDetails?.data?.data.show_date_of_birth,
        gender: getUserDetails?.data?.data.gender,
      });
      setOriginalUserName(getUserDetails?.data?.data.username);
    }
  }, [getUserDetails.data]);

  const [formErrors, setFormErrors] = useState({
    username: '',
    date_of_birth: '',
  });

  const validationSchema = Yup.object().shape({
    date_of_birth: Yup.date()
      .max(new Date())
      .min(new Date(1970, 0, 1)) // user has to be at least born later than january 1st 1970?
      .typeError('Invalid date format')
      .required('Birth date is required'),
  });

  const userNameValidationSchema = Yup.object().shape({
    newUserName: Yup.string()
      .trim()
      .min(8, 'Username must be minimum 8 characters')
      .max(50, 'Too Long!')
      .test(
        'use-allowable-characters',
        'Username cannot contain special characters',
        username => checkAllowableCharactersOnly(username as string),
      )
      .required('Enter your username'),
  });

  function handleCancel() {
    navigate('/profile');
  }

  const updateBasicInfoData = useMutation(
    (formData: IBasicInfoData) => UserServices.updateUserProfile(formData),
    {
      onSuccess(data) {
        if (data.data.error) {
          switch (data.data.error_code) {
            default:
              setShowSnackBar(true);
              break;
          }
        } else {
          getUserDetails.refetch();
          if (!userNameChanged) navigate('/profile');
        }
      },
      onError() {
        setShowSnackBar(true);
      },
    },
  );

  const updateUserName = useMutation(
    (newUserName: string) =>
      UserServices.updateUserProfile({ username: newUserName }),
    {
      onSuccess(data) {
        if (data.data.error) {
          switch (data.data.error_code) {
            case EC_USERNAME_MIN_CHAR_INVALID:
              setFormErrors({ ...formErrors, username: data.data.error_msg });
              break;
            case EC_SPECIAL_CHAR:
              setFormErrors({ ...formErrors, username: data.data.error_msg });
              break;
            case EC_INAPPROPRIATE:
              setFormErrors({ ...formErrors, username: data.data.error_msg });
              break;
            case EC_USER_USED:
              setFormErrors({ ...formErrors, username: data.data.error_msg });
              break;
            default:
              setShowSnackBar(true);
              break;
          }
        } else {
          getUserDetails.refetch();
          navigate('/profile');
        }
      },
    },
  );

  const handleSave = async () => {
    // empty error state first
    setFormErrors({
      username: '',
      date_of_birth: '',
    });

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      await updateBasicInfoData.mutateAsync(formData);

      if (userNameChanged) {
        await userNameValidationSchema.validate(
          { newUserName },
          { abortEarly: false },
        );

        await updateUserName.mutateAsync(newUserName);
      }
    } catch (validationErrors: unknown) {
      (validationErrors as ValidationError).inner.forEach(message => {
        if (message.path == 'newUserName') {
          setFormErrors(previousState => ({
            ...previousState,
            username: message.message,
          }));
        } else {
          setFormErrors(previousState => ({
            ...previousState,
            [message.path ?? '']: message.message,
          }));
        }
      });
    }
  };

  return (
    <div>
      {showSnackBar ? (
        <Snackbar
          message={'Oops! Something went wrong. Please try again later.'}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackBar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : (
        <></>
      )}
      <div className={'mb-6'}>
        <Input
          mode={'dark'}
          label={'Username'}
          required={true}
          className={'max-w-[352px] max-md:max-w-full'}
          value={originalUserName}
          onChange={event => {
            setNewUserName(event.target.value);
            setOriginalUserName(event.target.value);
            setUserNameChanged(true);
            setFormErrors({ ...formErrors, username: '' });
            setDisableButton(false);
          }}
          error={formErrors.username ? formErrors.username : ''}
        />
      </div>
      <div className={'mb-4'}>
        <Input
          mode={'dark'}
          label={'Date of Birth'}
          type={'date'}
          required={true}
          className={'max-w-[352px] max-md:max-w-full date-picker'}
          value={formData.date_of_birth}
          onChange={event => {
            setFormData({ ...formData, date_of_birth: event.target.value });
            setDisableButton(false);
          }}
          error={formErrors.date_of_birth ? formErrors.date_of_birth : ''}
        />
      </div>
      <div className={'mb-6 [&>label>div>svg]:!h-6'}>
        <Checkbox
          variant={'dark'}
          label={'Show date of birth'}
          isChecked={Boolean(Number(formData.show_date_of_birth))}
          isValidation={false}
          onChange={() => {
            setFormData({
              ...formData,
              show_date_of_birth: Number(formData.show_date_of_birth) ? 0 : 1,
            });
            setDisableButton(false);
          }}
        />
      </div>
      <div className={'mb-10'}>
        <p className={'mb-4'}>Gender</p>
        <div className={'flex'}>
          <Radio
            className={'mr-6'}
            variant={'dark'}
            isChecked={formData.gender == USER_GENDER_MALE}
            value={USER_GENDER_MALE}
            label={'Male'}
            isValidation={false}
            onChange={event => {
              setFormData({
                ...formData,
                gender: event.target.value as genderType,
              });
              setDisableButton(false);
            }}
          />
          <Radio
            variant={'dark'}
            isChecked={formData.gender === USER_GENDER_FEMALE}
            value={USER_GENDER_FEMALE}
            label={'Female'}
            isValidation={false}
            onChange={event => {
              setFormData({
                ...formData,
                gender: event.target.value as genderType,
              });
              setDisableButton(false);
            }}
          />
        </div>
      </div>
      <div className={'flex'}>
        <Button
          variant={'primary'}
          className={'font-black max-md:!w-full max-md:!max-w-full'}
          onClick={handleSave}
          disabled={disableButton}
        >
          Save
        </Button>
        <Button
          variant={'secondary'}
          className={'max-lg:hidden'}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default BasicInfoForm;
